import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import { $declinedAuthStore, $minimalBalanceStore, $usersListStore } from '@lib/store';
import { ValidateUtils } from '@shared/utils';
import { AlertTypeEnum, RecipientChannel } from '@shared/api/rest/alerts';
import { RadioInputOrientationTypeEnum } from '@shared/widgets/inputs/radio/models';
import { Select } from '@shared/widgets/select';
import { BaseInput, Button, ErrorMessage, Modal, RadioInput, Text } from '@shared/widgets';

enum RecipientType {
  OS = 'OS User',
  OTHER = 'Other',
}

const USER_TYPE_OPTIONS = [
  { text: RecipientType.OS, value: RecipientType.OS },
  { text: RecipientType.OTHER, value: RecipientType.OTHER },
];

interface IForm {
  user: string;
  name: string;
  email: string;
}
const DEFAULT_VALUES: IForm = { user: '', name: '', email: '' };

interface IAddRecipientModalProps {
  show: boolean;
  onClose: () => void;
  alertType: AlertTypeEnum;
}

export const AddRecipientModal = (props: IAddRecipientModalProps) => {
  const usersListStore = useUnit($usersListStore.store);
  const minimalBalanceStore = useUnit($minimalBalanceStore.store);
  const declinedAuthStore = useUnit($declinedAuthStore.store);

  const [userType, setUserType] = useState<RecipientType>(RecipientType.OS);

  const { control, handleSubmit, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (!props.show) return;
    $usersListStore.events.getUsers();
  }, [props.show]);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
    setUserType(RecipientType.OS);
  };
  const switchUserTypeHandler = (type: RecipientType) => {
    reset(DEFAULT_VALUES);
    setUserType(type);
  };
  const submitHandler = (data: IForm) => {
    if (props.alertType === AlertTypeEnum.minimalBalance) {
      $minimalBalanceStore.events.add({
        email: data.email,
        firstName: data.name,
        channel: RecipientChannel.EMAIL,
        userID: data.user || null,
      });
    } else {
      $declinedAuthStore.events.add({
        email: data.email,
        firstName: data.name,
        channel: RecipientChannel.EMAIL,
        userID: data.user || null,
      });
    }
    closeHandler();
  };

  const userList = useMemo(() => {
    switch (props.alertType) {
      case AlertTypeEnum.minimalBalance:
        return usersListStore.data
          .filter((user) => !minimalBalanceStore.recipient.find((recipient) => recipient.email === user.emailAddress))
          .map((user) => ({ value: user.id, label: `${user.firstName || ''} ${user.lastName || ''}` }));
      case AlertTypeEnum.declinedAuth:
        return usersListStore.data
          .filter((user) => !declinedAuthStore.recipient.find((recipient) => recipient.email === user.emailAddress))
          .map((user) => ({ value: user.id, label: `${user.firstName || ''} ${user.lastName || ''}` }));
      default:
        return usersListStore.data.map((user) => ({ value: user.id, label: `${user.firstName || ''} ${user.lastName || ''}` }));
    }
  }, [props.alertType, usersListStore.data, minimalBalanceStore.recipient, declinedAuthStore.recipient]);

  return (
    <Modal isOpen={props.show} onClose={closeHandler} title='New recipient'>
      <form className='flex flex-col gap-5' onSubmit={handleSubmit(submitHandler)}>
        <div>
          <Text className='mb-2'>User type</Text>
          <RadioInput
            orientation={RadioInputOrientationTypeEnum.Horizontal}
            options={USER_TYPE_OPTIONS}
            selected={userType}
            onChange={(value) => switchUserTypeHandler(value as RecipientType)}
          />
        </div>
        {userType === RecipientType.OS && (
          <Controller
            name='user'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text>Name</Text>
                <Select
                  items={userList}
                  nullValue={{ isEnable: false, text: 'Select recipient' }}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    const user = usersListStore.data.find((user) => user.id === e)!;
                    setValue('name', `${user.firstName} ${user.lastName}`);
                    setValue('email', user.emailAddress);
                  }}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {userType === RecipientType.OTHER && (
          <Controller
            name='name'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder='Name and Surname' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <div>
          <Controller
            name='email'
            control={control}
            rules={{
              required: ValidateUtils.requiredErrorMessage,
              pattern: { value: ValidateUtils.emailRegex, message: ValidateUtils.emailErrorMessage },
              validate: (value) => {
                const match =
                  (props.alertType === AlertTypeEnum.declinedAuth && declinedAuthStore.recipient.find((recipient) => recipient.email === value)) ||
                  (props.alertType === AlertTypeEnum.minimalBalance && minimalBalanceStore.recipient.find((recipient) => recipient.email === value));
                if (match) return 'This user already exists';
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput
                  {...field}
                  className={clsx(userType === RecipientType.OS && 'pointer-events-none opacity-60')}
                  placeholder='Enter e-mail'
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        </div>
        <Button className='mt-7' fullWidth color='primary' type='submit'>
          Add recipient
        </Button>
      </form>
    </Modal>
  );
};
